import {useTranslation} from 'react-i18next'
import {AtajoNumero, OPCION_BASE, OPCION_OFF} from '@/components/bateria/tira-opciones'
import {cn} from '@/lib/utils'
import {escalaDeSeccion, type SeccionClave} from '@/lib/bateria-cola'
import {Hint} from '@/components/ui/hint'

/** v2 §1.5. `0`, `←`, `→` y `D` significan siempre lo mismo y van en la fila común. */
const TECLAS_POR_SECCION: Record<SeccionClave, string[]> = {
  ubicar: ['candidato', 'buscar', 'descartar'],
  marcas_5: ['opcion', 'blanco', 'tachon', 'multiple'],
  marcas_4: ['opcion', 'blanco', 'tachon', 'multiple'],
  // `anios` es la tecla de la tarjeta compuesta de antigüedad.
  confirmar: ['chip', 'blanco', 'tachon', 'multiple', 'anios', 'aceptar'],
  escribir: ['escribir', 'aceptar', 'soltar'],
  // Son marcas leídas de la hoja completa en vez del recorte.
  sin_recorte: ['opcion', 'blanco', 'tachon', 'multiple'],
}

function Tecla({children}: {children: React.ReactNode}) {
  return (
    <kbd className="rounded border border-border bg-secondary px-1.5 py-0.5 font-mono text-[11px] text-foreground">
      {children}
    </kbd>
  )
}

function WidgetDeSeccion({seccion}: {seccion: SeccionClave}) {
  const {t} = useTranslation()
  const escala = escalaDeSeccion(seccion)

  if (escala) {
    return (
      <div className="flex flex-wrap items-center gap-1.5" data-testid="mapa-widget">
        {escala.map((valor, i) => (
          <span key={valor} className={cn(OPCION_BASE, OPCION_OFF, 'inline-flex items-center gap-1')}>
            <AtajoNumero n={i + 1} />
            {t(`admin.bateria.escala.${valor}`)}
          </span>
        ))}
      </div>
    )
  }

  if (seccion === 'confirmar') {
    return (
      <div className="flex flex-wrap items-center gap-1.5" data-testid="mapa-widget">
        {[1, 2, 3].map((n) => (
          <span key={n} className={cn(OPCION_BASE, OPCION_OFF, 'inline-flex items-center gap-1')}>
            <AtajoNumero n={n} />
            {t('admin.bateria.cola.mapa.chipEjemplo', {n})}
          </span>
        ))}
        <span className={cn(OPCION_BASE, OPCION_OFF, 'border-dashed')}>{t('admin.bateria.cola.mapa.propuesta')}</span>
      </div>
    )
  }

  if (seccion === 'escribir') {
    return (
      <div className="flex items-center gap-2" data-testid="mapa-widget">
        <span className="rounded-lg border border-input px-2.5 py-1.5 font-mono text-xs text-muted-foreground">
          {t('admin.bateria.cola.mapa.inputEjemplo')}
        </span>
      </div>
    )
  }

  // Mezcla marcas de las dos escalas: no hay una tira que pintar sin mentirle a la mitad.
  if (seccion === 'sin_recorte') {
    return <Hint testId="mapa-widget">{t('admin.bateria.cola.mapa.sinRecorteWidget')}</Hint>
  }

  return (
    <ol className="space-y-1" data-testid="mapa-widget">
      {[1, 2, 3].map((n) => (
        <li key={n} className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-mono">{n}</span>
          {t('admin.bateria.cola.mapa.candidatoEjemplo', {n})}
        </li>
      ))}
    </ol>
  )
}

export function MapaTeclas({seccion}: {seccion: SeccionClave}) {
  const {t} = useTranslation()
  return (
    <div className="space-y-3" data-testid={`mapa-teclas-${seccion}`}>
      <WidgetDeSeccion seccion={seccion} />
      <ul className="space-y-1 text-xs text-muted-foreground">
        {TECLAS_POR_SECCION[seccion].map((tecla) => (
          <li key={tecla} className="flex items-start gap-2">
            <Tecla>{t(`admin.bateria.cola.mapa.tecla.${tecla}.key`)}</Tecla>
            <span>{t(`admin.bateria.cola.mapa.tecla.${tecla}.que`)}</span>
          </li>
        ))}
        {['navegar', 'despues', 'porMarcar', 'ayuda'].map((tecla) => (
          <li key={tecla} className="flex items-start gap-2">
            <Tecla>{t(`admin.bateria.cola.mapa.tecla.${tecla}.key`)}</Tecla>
            <span>{t(`admin.bateria.cola.mapa.tecla.${tecla}.que`)}</span>
          </li>
        ))}
      </ul>
      {(seccion === 'marcas_5' || seccion === 'marcas_4' || seccion === 'sin_recorte') && (
        <p className="notice-amber rounded-md px-3 py-2 text-xs" data-testid="mapa-enter-no-acepta">
          {t('admin.bateria.cola.mapa.enterNoAcepta')}
        </p>
      )}
    </div>
  )
}
